import { supabase } from './supabase';

export interface ProjectService {
  id: string;
  project_id: string;
  service_id: string;
  vehicle_id?: string | null;
  name: string;
  description?: string | null;
  price: number;
  tax_exempt: boolean;
  tax_rate?: number | null;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  duration_minutes: number;
  price_option_id?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  service?: {
    id: string;
    name: string;
    description?: string | null;
  };
  vehicle?: {
    id: string;
    make: string;
    model: string;
    year: number;
    color?: string | null;
  };
}

export interface AddProjectServiceParams {
  project_id: string;
  service_id: string;
  vehicle_id?: string | null;
  name: string;
  description?: string | null;
  price: number;
  tax_exempt?: boolean;
  tax_rate?: number | null;
  tax_amount?: number;
  discount_amount?: number;
  total_amount?: number;
  duration_minutes: number;
  price_option_id?: string | null;
  notes?: string | null;
}

/**
 * Get all services for a project
 */
export async function getProjectServices(projectId: string): Promise<ProjectService[]> {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  const { data, error } = await supabase
    .from('project_services')
    .select(`
      *,
      service:services(
        id,
        name,
        description
      ),
      vehicle:customer_vehicles(
        id,
        make,
        model,
        year,
        color
      )
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching project services:', error);
    throw error;
  }

  return (data || []) as ProjectService[];
}

/**
 * Add a service to a project
 */
export async function addProjectService(params: AddProjectServiceParams): Promise<ProjectService> {
  if (!params.project_id) {
    throw new Error('Project ID is required');
  }
  if (!params.service_id) {
    throw new Error('Service ID is required');
  }

  // Calculate tax and total if not provided
  const taxRate = params.tax_rate || 0;
  const taxAmount = params.tax_exempt ? 0 : Math.round((params.price * taxRate / 100) * 100) / 100;
  const discountAmount = params.discount_amount || 0;
  const totalAmount = Math.round((params.price - discountAmount + taxAmount) * 100) / 100;

  const { data, error } = await supabase
    .from('project_services')
    .insert({
      project_id: params.project_id,
      service_id: params.service_id,
      vehicle_id: params.vehicle_id || null,
      name: params.name,
      description: params.description || null,
      price: params.price,
      tax_exempt: params.tax_exempt || false,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      discount_amount: discountAmount,
      total_amount: totalAmount,
      duration_minutes: params.duration_minutes,
      price_option_id: params.price_option_id || null,
      notes: params.notes || null,
    })
    .select(`
      *,
      service:services(
        id,
        name,
        description
      ),
      vehicle:customer_vehicles(
        id,
        make,
        model,
        year,
        color
      )
    `)
    .single();

  if (error) {
    console.error('Error adding project service:', error);
    throw error;
  }

  return data as ProjectService;
}

/**
 * Remove a service from a project
 */
export async function removeProjectService(serviceId: string): Promise<void> {
  if (!serviceId) {
    throw new Error('Service ID is required');
  }

  const { error } = await supabase
    .from('project_services')
    .delete()
    .eq('id', serviceId);

  if (error) {
    console.error('Error removing project service:', error);
    throw error;
  }
}
